/**
 * Design token constants — single source of truth for priority, status, health, urgency.
 * Color is never the only signal: every variant includes both a color class and a label.
 */

export const PRIORITY = {
  low: {
    label: "Low",
    dot: "bg-gray-400",
    badge: "bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-800 dark:text-gray-300",
    icon: "●",
  },
  medium: {
    label: "Medium",
    dot: "bg-blue-500",
    badge: "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300",
    icon: "●",
  },
  high: {
    label: "High",
    dot: "bg-orange-500",
    badge: "bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950 dark:text-orange-300",
    icon: "▲",
  },
  critical: {
    label: "Critical",
    dot: "bg-red-500",
    badge: "bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300",
    icon: "⚠",
  },
} as const

export const HEALTH = {
  on_track: {
    label: "On Track",
    dot: "bg-green-500",
    badge: "bg-green-50 text-green-700 border-green-200 dark:bg-green-950 dark:text-green-300",
    icon: "✓",
  },
  at_risk: {
    label: "At Risk",
    dot: "bg-amber-500",
    badge: "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950 dark:text-amber-300",
    icon: "⚠",
  },
  off_track: {
    label: "Off Track",
    dot: "bg-red-500",
    badge: "bg-red-50 text-red-700 border-red-200 dark:bg-red-950 dark:text-red-300",
    icon: "✕",
  },
} as const

export const URGENCY = {
  critical: {
    label: "Critical",
    badge: "bg-red-50 text-red-700 border-red-300 dark:bg-red-950 dark:text-red-300",
  },
  high: {
    label: "High",
    badge: "bg-orange-50 text-orange-700 border-orange-300 dark:bg-orange-950 dark:text-orange-300",
  },
  medium: {
    label: "Medium",
    badge: "bg-amber-50 text-amber-700 border-amber-300 dark:bg-amber-950 dark:text-amber-300",
  },
  low: {
    label: "Low",
    badge: "bg-gray-50 text-gray-700 border-gray-300 dark:bg-gray-800 dark:text-gray-300",
  },
} as const

export const INSIGHT_TYPE = {
  risk: { label: "Risk", badge: "bg-red-50 text-red-700 border-red-200" },
  bottleneck: { label: "Bottleneck", badge: "bg-orange-50 text-orange-700 border-orange-200" },
  efficiency: { label: "Efficiency", badge: "bg-green-50 text-green-700 border-green-200" },
  prediction: { label: "Prediction", badge: "bg-purple-50 text-purple-700 border-purple-200" },
  recommendation: { label: "Recommendation", badge: "bg-blue-50 text-blue-700 border-blue-200" },
} as const

export type Priority = keyof typeof PRIORITY
export type Health = keyof typeof HEALTH
export type Urgency = keyof typeof URGENCY
export type InsightType = keyof typeof INSIGHT_TYPE
